/* eslint-disable */
/**
 * Public Blog Detail Page — /blog/:slug
 * Renders a single blog post with markdown-like formatting.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, Clock, User, Share2, BookOpen, Link as LinkIcon, ChevronRight, Check, ArrowRight, MessageCircle, Send } from 'lucide-react';
import { useBlog } from '../context/BlogContext';
import Navbar from '../components/landing-page/Navbar';

/** Very light markdown renderer (bold, headings, lists, paragraphs) */
function renderContent(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-2xl sm:text-3xl font-bold text-white mt-12 mb-6 leading-snug tracking-tight">
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="text-xl sm:text-2xl font-semibold text-gold-300 mt-8 mb-4">
          {line.slice(4)}
        </h3>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(
          <li key={i} className="flex gap-4 text-navy-200/90 text-base sm:text-lg leading-relaxed mb-3">
            <span className="text-gold-500 mt-1.5 flex-shrink-0">
              <Check size={18} />
            </span>
            <span dangerouslySetInnerHTML={{ __html: boldify(lines[i].slice(2)) }} />
          </li>
        );
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="flex flex-col gap-2 my-6 bg-white/[0.02] p-6 sm:p-8 rounded-3xl border border-white/5">{items}</ul>);
      continue;
    } else if (/^\d+\. /.test(line)) {
      const items = [];
      let num = 1;
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(
          <li key={i} className="flex gap-4 text-navy-200/90 text-base sm:text-lg leading-relaxed mb-4">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold-500/10 text-gold-400 font-bold flex-shrink-0 text-sm border border-gold-500/20">{num}</span>
            <span className="pt-1" dangerouslySetInnerHTML={{ __html: boldify(lines[i].replace(/^\d+\. /, '')) }} />
          </li>
        );
        i++;
        num++;
      }
      elements.push(<ol key={`ol-${i}`} className="flex flex-col gap-2 my-8">{items}</ol>);
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-4" />);
    } else {
      elements.push(
        <p key={i} className="text-navy-200/90 text-base sm:text-lg leading-relaxed mb-6 font-light"
          dangerouslySetInnerHTML={{ __html: boldify(line) }}
        />
      );
    }
    i++;
  }
  return elements;
}

function boldify(str) {
  return str.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getPost, publishedPosts } = useBlog();
  const [copied, setCopied] = useState(false);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const post = getPost(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-navy-950 flex flex-col items-center justify-center gap-6 selection:bg-gold-500/30">
        <div className="w-24 h-24 rounded-full bg-white/[0.02] border border-white/5 flex items-center justify-center shadow-inner">
          <BookOpen size={40} className="text-navy-500" />
        </div>
        <p className="text-white text-xl font-medium">Artikel tidak ditemukan.</p>
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gold-400 transition-all font-semibold"
        >
          <ArrowLeft size={18} /> Kembali ke Pusat Wawasan
        </button>
      </div>
    );
  }

  // Related posts — same category, different post
  const related = publishedPosts
    .filter((p) => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-navy-950 relative font-sans selection:bg-gold-500/30">
      <Navbar />
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1.5 bg-gold-500 origin-left z-50 shadow-[0_0_10px_rgba(212,149,14,0.5)]"
        style={{ scaleX }}
      />

      {/* Ambient bg */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute top-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-gold-500/5 blur-[120px]" />
        <div className="absolute bottom-[10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-900/10 blur-[150px]" />
      </div>

      {/* ── Cover Hero ── */}
      <div className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-navy-300 hover:text-white text-sm mb-8 transition-colors group px-4 py-2 bg-white/5 hover:bg-white/10 rounded-full border border-white/5 backdrop-blur-sm"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Kembali
          </button>

          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-block bg-gradient-to-r from-gold-500/20 to-gold-500/10 border border-gold-500/30 text-gold-400 text-xs font-bold px-4 py-1.5 rounded-full shadow-[0_0_15px_rgba(212,149,14,0.15)] uppercase tracking-wider backdrop-blur-md">
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-navy-200 text-xs font-semibold px-4 py-1.5 rounded-full backdrop-blur-md">
              <Clock size={12} className="text-gold-400" />
              {post.readTime} Membaca
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.2] tracking-tight mb-8">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center justify-between gap-6 py-6 border-y border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-navy-950 font-bold text-lg shadow-lg border-2 border-navy-950">
                {post.author.charAt(0)}
              </div>
              <div>
                <div className="text-white font-semibold text-base">{post.author}</div>
                <div className="text-navy-400 text-sm">
                  {new Date(post.publishedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-navy-400 text-sm mr-2 font-medium hidden sm:block">Bagikan:</span>
              <button onClick={handleCopyLink} className="w-10 h-10 rounded-full bg-white/5 hover:bg-gold-500 hover:text-[#060c1a] border border-white/10 flex items-center justify-center text-navy-300 transition-all group relative">
                {copied ? <Check size={16} className="text-green-400 group-hover:text-[#060c1a]" /> : <LinkIcon size={16} />}
              </button>
              <button className="w-10 h-10 rounded-full bg-white/5 hover:bg-green-500 hover:border-green-500 border border-white/10 flex items-center justify-center text-navy-300 transition-all hover:text-white">
                <MessageCircle size={16} />
              </button>
              <button className="w-10 h-10 rounded-full bg-white/5 hover:bg-sky-500 hover:border-sky-500 border border-white/10 flex items-center justify-center text-navy-300 transition-all hover:text-white">
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Featured Image ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10 mb-16">
        <div
          className="w-full h-64 sm:h-[400px] lg:h-[500px] rounded-[2rem] overflow-hidden relative shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10"
          style={{ background: post.coverColor || 'linear-gradient(135deg,#1a2d5a,#0a1628)' }}
        >
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent mix-blend-overlay" />
          <div className="absolute inset-0 flex items-center justify-center">
            <BookOpen size={100} className="text-white/10 drop-shadow-2xl" />
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-24"
      >
        {/* Excerpt / lead */}
        <div className="bg-gradient-to-r from-gold-500/10 to-transparent p-1 rounded-r-2xl mb-12 border-l-4 border-gold-500">
          <p className="text-white text-lg sm:text-xl leading-relaxed font-medium p-6 sm:p-8 bg-navy-950/80 backdrop-blur-sm rounded-r-xl">
            {post.excerpt}
          </p>
        </div>

        {/* Body */}
        <div className="prose prose-invert prose-lg max-w-none prose-headings:font-bold prose-a:text-gold-400 hover:prose-a:text-gold-300">
          {renderContent(post.content)}
        </div>

        {/* Post Footer CTA */}
        <div className="mt-20 rounded-3xl relative overflow-hidden bg-gradient-to-br from-[#0a1628] to-[#0f1c3f] border border-gold-500/20 shadow-2xl">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-gold-500 via-transparent to-transparent" />
          <div className="p-8 sm:p-12 text-center relative z-10">
            <h3 className="text-2xl font-bold text-white mb-3">Butuh Konsultasi Hukum Lebih Lanjut?</h3>
            <p className="text-navy-300 text-base mb-8 max-w-lg mx-auto">
              Tim pengacara ahli kami siap membantu Anda menyelesaikan masalah hukum dengan strategi terbaik.
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 bg-gold-500 text-navy-950 font-bold text-base px-8 py-4 rounded-xl shadow-[0_10px_30px_rgba(212,149,14,0.3)] hover:shadow-[0_15px_40px_rgba(212,149,14,0.4)] hover:-translate-y-1 transition-all duration-300"
            >
              Hubungi Kami Sekarang <ChevronRight size={20} />
            </a>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-24 pt-12 border-t border-white/10">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-white">Artikel Terkait</h2>
              <button
                onClick={() => navigate('/blog')}
                className="text-gold-400 hover:text-gold-300 text-sm font-semibold flex items-center gap-1 transition-colors"
              >
                Lihat Semua <ArrowRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {related.map((r) => (
                <div
                  key={r.id}
                  onClick={() => navigate(`/blog/${r.slug}`)}
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-white/[0.02] border border-white/5 hover:border-gold-500/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_15px_30px_-10px_rgba(212,149,14,0.2)] flex flex-col"
                >
                  <div className="h-32 relative overflow-hidden" style={{ background: r.coverColor || 'linear-gradient(135deg,#1a2d5a,#0a1628)' }}>
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" />
                    <BookOpen size={24} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/10" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-gold-400 text-[10px] font-bold uppercase tracking-wider mb-2">{r.category}</span>
                    <h3 className="text-white text-lg font-bold mb-3 group-hover:text-gold-300 transition-colors line-clamp-2 leading-tight">
                      {r.title}
                    </h3>
                    <div className="mt-auto flex items-center gap-3 text-xs text-navy-400">
                      <span className="flex items-center gap-1.5">
                        <Clock size={12} className="text-gold-500" /> {r.readTime}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
